import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { IUser } from '../../../lib/interfaces';
import { DS, fadeInSx } from '../designTokens';
import { usePatientName } from '../hooks/usePatientName';
import { RemoteCallParticipant } from '../types';
import Avatar from './Avatar';
import VideoTile from './VideoTile';

type PlaceholderProps = {
  waiting: boolean;
  patient: IUser;
};

const PatientPlaceholder = ({ waiting, patient }: PlaceholderProps) => {
  const { t } = useTranslation();
  const patientName = usePatientName(patient);

  if (waiting) {
    return (
      <Box sx={{ textAlign: 'center', px: 3, ...fadeInSx }}>
        <CircularProgress sx={{ color: DS.linen, mb: 2 }} />
        <Typography
          sx={{ fontFamily: DS.fontBody, fontSize: 13, color: DS.onDarkMuted }}
        >
          {t('videoVisit.status.waitingForRemote', { name: patientName })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center', px: 3, ...fadeInSx }}>
      <Avatar name={patientName} />
      <Typography
        sx={{
          fontFamily: DS.fontBody,
          fontSize: 15,
          fontWeight: 700,
          color: DS.linen,
        }}
      >
        {patientName}
      </Typography>
    </Box>
  );
};

// In the PPoC the patient always joins from mobile-app, held upright
// (EASE2-603), so the frame starts portrait until the video reports its size.
const DEFAULT_ASPECT_RATIO = 9 / 16;

type Props = {
  patient: IUser;
  remote?: RemoteCallParticipant;
};

const PatientTile = ({ patient, remote }: Props) => {
  // Kept while the camera is off, so the frame keeps the patient's shape.
  const [aspectRatio, setAspectRatio] = useState(DEFAULT_ASPECT_RATIO);
  const showVideo = remote !== undefined && !remote.cameraOff;

  useEffect(() => {
    setAspectRatio(DEFAULT_ASPECT_RATIO);
  }, [remote?.participant.identity]);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        containerType: 'size',
      }}
    >
      <VideoTile
        track={remote?.camera}
        showVideo={showVideo}
        onAspectRatio={setAspectRatio}
        placeholder={
          <PatientPlaceholder
            waiting={remote === undefined}
            patient={patient}
          />
        }
        sx={{
          width: `min(100cqw, calc(100cqh * ${aspectRatio}))`,
          height: `min(100cqh, calc(100cqw / ${aspectRatio}))`,
          borderRadius: '16px',
          background: showVideo ? DS.letterbox : DS.frame,
        }}
      />
    </Box>
  );
};

export default PatientTile;
